import { DatePicker, Form, Input, Select, TimePicker } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { Constants } from '../constants/general.constant';
import { useGet } from '../hooks/useRest.hook';
import { debounce } from 'debounce';
import { fetchGooglePlacePredictions, isGooglePlacesReady } from '../utils/googlePlaces';
export const MYCInput = ({ name, label, disabled, hidden, value, labelColor, required, className, noMb, type = "text", min, minLength }) => {
    const rules = [
        ...(required ? [{ message: `${label} is required`, required: true }] : []),
        ...(min ? [{ message: `${label} cannot be less than ${min}`, validator: (rule, value) => new Promise((resolve, reject) => parseInt(value) >= min ? resolve() : reject()) }] : []),
        ...(minLength ? [{ min: minLength, message: `${label} must be at least ${minLength} characters long` }] : []),
    ];
    return <Form.Item hidden={hidden} name={name} disabled={disabled} label={<label style={{ color: labelColor || "black" }}>{label}</label>} style={{ color: 'white' }} className={`${noMb ? '' : 'mb-0'} ${className}`} rules={rules.length > 0 ? rules : undefined}>
        <Input placeholder={label} disabled={disabled} value={value} type={type} />
    </Form.Item>
}


export const MYCSelect = ({ name, initalValue, label, options, required, className, noMb, type = "text", multiple = false, disabled = false }) => {
    return <Form.Item initialValue={initalValue} name={name} className={`${noMb ? '' : 'mb-0'} ${className}`} label={label} rules={required ? [{ message: `${label} is required`, required: true }] : undefined}>
        <Select
            options={options}
            placeholder={label}
            mode={multiple ? 'multiple' : undefined}
            disabled={disabled}
        ></Select>
    </Form.Item>
}

const MYCPlacesSelectAutocomplete = ({ name, label, hint, required, className, noMb }) => {
    const [placePredictions, setPlacePredictions] = useState([]);
    const [isPlacePredictionsLoading, setIsPlacePredictionsLoading] = useState(false);
    const [placesReady, setPlacesReady] = useState(isGooglePlacesReady());
    const requestSequenceRef = useRef(0);

    useEffect(() => {
        if (placesReady) return undefined;

        const interval = setInterval(() => {
            if (isGooglePlacesReady()) {
                setPlacesReady(true);
                clearInterval(interval);
            }
        }, 250);

        return () => clearInterval(interval);
    }, [placesReady]);

    const handleSearch = async (search) => {
        const requestId = requestSequenceRef.current + 1;
        requestSequenceRef.current = requestId;

        const normalizedSearch = (search || '').trim();
        if (!normalizedSearch) {
            setPlacePredictions([]);
            setIsPlacePredictionsLoading(false);
            return;
        }

        setIsPlacePredictionsLoading(true);
        const predictions = await fetchGooglePlacePredictions(normalizedSearch);
        if (requestId !== requestSequenceRef.current) return;

        setPlacePredictions(predictions);
        setIsPlacePredictionsLoading(false);
    }

    return <Form.Item name={name} className={`${noMb ? '' : 'mb-0'} ${className}`} label={label} rules={required ? [{ message: `${label} is required`, required: true }] : undefined}>
        <Select
            showSearch
            placeholder={hint || label}
            disabled={!placesReady}
            loading={isPlacePredictionsLoading}
            defaultActiveFirstOption={false}
            className={hint && "hint"}
            filterOption={false}
            onSearch={handleSearch}
            notFoundContent={placesReady ? null : 'Google Places unavailable'}
            options={(placePredictions || []).map((d) => ({
                value: d.place_id,
                label: d.description,
            }))}
        />
    </Form.Item>
}

export const MYCPlacesSelect = ({ name, label,hint, options, required, className, noMb, type = "text" }) => {
    if (!Constants.GOOGLE_API_KEY) {
        return (
            <Form.Item
                name={name}
                className={`${noMb ? '' : 'mb-0'} ${className}`}
                label={label}
                rules={[
                    {
                        validator: () =>
                            Promise.reject(
                                new Error("Google Places is not configured (missing REACT_APP_GOOGLE_MAPS_API_KEY)")
                            ),
                    },
                ]}
            >
                <Input disabled placeholder="Missing Google Places configuration" />
            </Form.Item>
        );
    }
    return <MYCPlacesSelectAutocomplete name={name} label={label} hint={hint} required={required} className={className} noMb={noMb} />
}

export const MYCRemoteSearchSelect = ({ name,onlyActive, label, multiple, disabled, options, required, searchAttr, className, noMb, type = "text", url, labelDef, valueDef }) => {
    const [search, setSearch] = useState('');
    const [query, setQuery] = useState({});
    const searchRq = useGet(url, true, query);
    useEffect(() => {
        setQuery({
            [searchAttr || 'search']: search,
            onlyActive
        });
    }, [onlyActive, search, searchAttr])
    const handleSearch = debounce(setSearch, 300);

    return <Form.Item name={name} disabled={disabled} className={`${noMb ? '' : 'mb-0'} ${className}`} label={label} rules={required ? [{ message: `${label} is required`, required: true }] : undefined}>
        <Select
            showSearch
            disabled={disabled}
            placeholder={label}
            className={className}
            mode={multiple ? 'multiple' : undefined}
            loading={searchRq?.loading}
            defaultActiveFirstOption={false}
            allowClear
            filterOption={false}
            onSearch={handleSearch}
            notFoundContent={null}
            options={(searchRq?.data?.list || []).map((d) => ({
                value: valueDef?.(d) || d?.id,
                label: labelDef?.(d) || d?.name,
            }))}
        />
    </Form.Item>
}


export const MYCSearch = ({ name, disabled, label, required, className, noMb, type = "text" }) => {
    return <Form.Item name={name} label={label} className={`${noMb ? '' : 'mb-0'} ${className}`} rules={required ? [{ message: `${label} is required`, required: true }] : undefined}>
        <Input.Search disabled={disabled} placeholder={label} type={type} />
    </Form.Item>
}

export const MYCTextArea = ({ name, label, initialValue, required, className, noMb, type = "text" }) => {
    return <Form.Item initialValue={initialValue} name={name} label={label} className={`${noMb ? '' : 'mb-0'} ${className}`} rules={required ? [{ message: `${label} is required`, required: true }] : undefined}>
        <Input.TextArea rows={4} placeholder={label} type={type} />
    </Form.Item>
}

export const MYCDate = ({ label, required, className, name, showTime = true }) => {
    return <Form.Item label={label} className={`mb-0 ${className}`} name={name} rules={required ? [{ message: `${label} is required`, required: true }] : undefined}>
        <DatePicker placeholder={label} style={{ width: '100%' }} format={showTime ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY"} showTime={showTime} />
    </Form.Item>
}

export const MYCTimePicker = ({ label, required, className, name }) => {
    return <Form.Item label={label} className={`mb-0 ${className}`} name={name} rules={required ? [{ message: `${label} is required`, required: true }] : undefined}>
        <TimePicker use12Hours style={{ width: '100%' }} placeholder={label} className={className} />
    </Form.Item>
}
